import "./partListing.scss";

import { PageProps, graphql } from "gatsby";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";

import Loading from "../../components/primitives/Loading";
import { LocalizedLink } from "../../components/primitives";
import { PartPreview } from "./PartPreview";
import SEO from "../../components/Seo";
import WZoom from "vanilla-js-wheel-zoom/src/wheel-zoom";
import useMobileDetect from "../../hooks/useMobileDetect";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { IProduct, ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services";
import { getAvailability, productLiveAvailabilityLabel } from "../../utils/productAvailabilityInfo";
import { AddItemButton } from "@sbi-international/boutik.gatsby.plugin.checkout";
import MainLayout from "../../components/layout/MainLayout";
import { ProductStock } from "@sbi-international/boutik.gatsby.plugin.components";
import { PartsFilteredPart } from "../../models/partsFilteredPart";
import LoadingFont from "../../components/primitives/LoadingFont";

const isBrowser = typeof window !== "undefined";

export const query = graphql`
    query PartListingTemplate($code: String!) {
        productsJson(code: { eq: $code }) {
            code
            id
            title {
                en_CA
                en_US
                fr_CA
            }
            schema {
                en_CA
                en_US
                fr_CA
            }
            fields {
                schema {
                    childImageSharp {
                        gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH)
                    }
                }
            }
            parts {
                code
                id
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                slug {
                    fr_CA
                    en_CA
                    en_US
                }
                attributes {
                    custom_made {
                        group
                        name {
                            en_US
                            en_CA
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    schema_number {
                        group
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    nb_per_appliance {
                        group
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function PartListing({ data }: PageProps<Queries.PartListingTemplateQuery>) {
    const product = data.productsJson as any;
    let parts = product?.parts as any;
    const [explodedParts, setExplodedParts] = useState<PartsFilteredPart[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;

    const { currentLocale, navigateTranslated } = usePageLocalization();
    const { t } = useTranslation("templatePages");

    const comparePart = (a: any, b: any) => {
        let intervalStart;

        if (a?.includes(",")) {
            intervalStart = a?.substring(0, 2);
            return intervalStart - b;
        }

        if (b?.includes(",")) {
            intervalStart = b?.substring(0, 2);
            return a - intervalStart;
        }

        if (a?.includes("-") || b?.includes("-")) {
            if (a?.includes("-") && b?.includes("-")) {
                return a - b;
            } else if (a?.includes("-")) {
                return 1;
            } else {
                return -1;
            }
        }

        return a - b;
    };

    const Part = ({ part }: any) => {
        const hasOwnPage =
            !!part.part.slug?.[currentLocale] && !part.part.slug?.[currentLocale].includes("9999");

        const clickTd = () => {
            navigateTranslated(part?.part?.slug?.[currentLocale]);
        };

        return (
            <tr>
                <td className="part-number">
                    <span>{part.part.attributes.schema_number?.values[0][currentLocale] ?? 0}</span>
                </td>
                <td
                    className={hasOwnPage ? "td hasLink" : "td"}
                    onClick={() => {
                        hasOwnPage && clickTd();
                    }}
                >
                    {part.part.code}
                    <br />
                    <strong>{part.part.title && part.part.title[currentLocale]}</strong>
                    <br />
                    {part.part.attributes.nb_per_appliance?.values[0][currentLocale]}{" "}
                    {t("nb-per-appliance")}
                </td>
                <td className="tdPriceAndStock">
                    {part.part.code != "99997" &&
                        part.part.code != "99998" &&
                        part.part.code != "99999" && (
                            <div className="td-price-and-stock-container">
                                <div className="price">
                                    {part?.currentPrice?.regularPrice?.formattedValue}
                                </div>
                                <div className="stock">
                                    <ProductStock
                                        currentStock={part?.currentStock}
                                        type="medium-list"
                                        isCustomMade={false}
                                    />
                                </div>
                            </div>
                        )}
                </td>
                <td className="part-cart-button">
                    {part.code}
                    <div className="addToCart">
                        <AddItemButton
                            type={"medium"}
                            sku={part.part.code}
                            availability={getAvailability(part?.currentStock)}
                        />
                    </div>
                </td>
            </tr>
        );
    };

    useEffect(() => {
        let newExplodedParts: PartsFilteredPart[] = [];
        parts.forEach((part: any) => {
            const schemaNumber = part.attributes.schema_number?.values[0][currentLocale];

            if (schemaNumber?.includes(",")) {
                schemaNumber.split(",").forEach((partNumber: any) => {
                    let newPart = JSON.parse(JSON.stringify(part));

                    newPart.attributes.schema_number.values[0][currentLocale] = partNumber;

                    newExplodedParts.push({
                        part: newPart,
                        currentStock: null,
                        currentPrice: null,
                    });
                });
            } else {
                newExplodedParts.push({ part: part, currentStock: null, currentPrice: null });
            }
        });
        setIsLoading(true);
        productService.operations
            .getProducts(newExplodedParts.map((part: any) => part.part.code))
            .then((productsData) => {
                for (const productData of productsData) {
                    const index = newExplodedParts.findIndex(
                        (part: any) => part.part.code === productData.sku
                    );
                    newExplodedParts[index].currentStock = productData.currentStock;
                    newExplodedParts[index].currentPrice = productData.currentPrice;
                }
                setExplodedParts(newExplodedParts);
            })
            .finally(() => setIsLoading(false));
    }, [parts]);

    const imgRef: any = useRef();
    const [wzoom, setWzoom] = useState<any>({});
    const isMobile = useMobileDetect().isMobile();
    const [pdfScale, setPdfScale] = useState(isMobile ? 80 : 100);
    const [coords, setCoords] = useState<any[]>([]);
    const [pdfPage, setPdfPage] = useState<any | null>(null);
    const [pageNumber] = useState(1);
    const [pdfAnnotations, setAnnotations] = useState<any[]>([]);
    const [schemaIndex, setSchemaIndex] = useState(0);

    useEffect(() => {
        if (isBrowser) {
            setWzoom(1); // Force un refresh du component
            for (let index = 0; index < pdfAnnotations.length; index++) {
                const rect = pdfAnnotations[index].rect;
                const view = pdfPage?.view;
                const normalizedRect = pdfjs.Util.normalizeRect([
                    rect[0],
                    view[3] - rect[1],
                    rect[2],
                    view[3] - rect[3],
                ]);
                const newCoord = {
                    x: normalizedRect[0],
                    y: normalizedRect[1],
                    note: pdfAnnotations?.[index]?.unsafeUrl,
                };
                const newCoords = coords;
                newCoords.push(newCoord);
                setCoords(newCoords);
                console.log("newCoords");
                console.log(newCoords);
            }
        }
    }, [coords, pdfAnnotations, pdfPage?.view]);

    async function getSchemaAnnotations(schema: any) {
        const pdfDocument = await pdfjs.getDocument(`/file/${schema}`).promise.then((doc: any) => {
            return doc;
        });
        const page = await pdfDocument.getPage(1);
        const annotations = await page.getAnnotations();
        setPdfPage(page);
        setAnnotations(annotations);
    }
    function updateSchemaIndex(modifier: any) {
        if (schemaIndex + modifier >= 0 && schemaIndex + modifier < product?.schema?.length)
            setSchemaIndex(schemaIndex + modifier);
    }
    function updatePdfScale(modifier: any) {
        if (pdfScale + modifier > 0 && pdfScale + modifier < 200) setPdfScale(pdfScale + modifier);
    }

    const endsWithPdf = product?.schema?.[schemaIndex]?.[currentLocale]?.endsWith(".pdf");
    const hasMultipleSchema = product?.schema?.length > 1;
    // la

    useEffect(() => {
        if (isBrowser) {
            if (!endsWithPdf) {
                setWzoom(
                    WZoom.create("#parts-img" as any, {
                        maxScale: 10,
                        minScale: 0.35,
                        speed: 2,
                    })
                );
            }
            pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
        }
    }, []);

    const sku = product.code;
    const productTitle = product.title[currentLocale];

    return (
        <MainLayout>
            <SEO
                title={t("metadata:part.title", { SKU: sku })}
                description={
                    t("metadata:part.description", {
                        TITLE: productTitle,
                        SKU: sku,
                    }) as string
                }
                isDescriptionCurrentLocale={true}
            />
            <div className="container-100">
                <div className="container part">
                    <LocalizedLink className="return-to-parts" to="/archives/">
                        <>
                            <span className="iconfont iconfont-return"></span>
                            {t("part-return-message")}
                        </>
                    </LocalizedLink>
                    <div className="row">
                        <div className="primary">
                            <header>
                                <div className="container part-header">
                                    {product.title && (
                                        <h1 className="part-title">
                                            {product.title[currentLocale]}
                                        </h1>
                                    )}
                                    <p>{product.code}</p>
                                </div>
                            </header>

                            <div className="schema-slider">
                                {endsWithPdf && hasMultipleSchema && (
                                    <span className="page">{`${schemaIndex + 1} / ${
                                        product?.schema?.length
                                    }`}</span>
                                )}
                                {endsWithPdf && hasMultipleSchema && (
                                    <FontAwesomeIcon
                                        className="arrow left"
                                        icon={faChevronLeft}
                                        onClick={() => updateSchemaIndex(-1)}
                                    />
                                )}
                                {endsWithPdf && (
                                    <div className="pdf-scale">
                                        <FontAwesomeIcon
                                            className="scale"
                                            icon={faPlus}
                                            onClick={() => updatePdfScale(20)}
                                        />
                                        <FontAwesomeIcon
                                            className="scale"
                                            icon={faMinus}
                                            onClick={() => updatePdfScale(-20)}
                                        />
                                    </div>
                                )}
                                {product?.schema && isBrowser ? (
                                    <div
                                        className={endsWithPdf ? "part-schema pdf" : "part-schema"}
                                    >
                                        {endsWithPdf ? (
                                            <Document
                                                className="pdf-doc"
                                                file={`/file/${product?.schema?.[schemaIndex]?.[currentLocale]}`}
                                                loading={<Loading />}
                                                onLoadError={(error: any) => {
                                                    console.log(error);
                                                }}
                                            >
                                                <Page
                                                    scale={pdfScale * 0.01}
                                                    pageNumber={pageNumber}
                                                    onLoadSuccess={() =>
                                                        getSchemaAnnotations(
                                                            product?.schema?.[schemaIndex]?.[
                                                                currentLocale
                                                            ]
                                                        )
                                                    }
                                                    onLoadError={(error: any) => {
                                                        console.log(error);
                                                    }}
                                                    renderAnnotationLayer={false}
                                                />

                                                {isLoading ? (
                                                    <div className="part-loading-container">
                                                        <LoadingFont />
                                                    </div>
                                                ) : (
                                                    coords.map((coord, index) => {
                                                        return (
                                                            <PartPreview
                                                                key={index}
                                                                coord={coord}
                                                                allParts={explodedParts}
                                                            />
                                                        );
                                                    })
                                                )}
                                            </Document>
                                        ) : (
                                            <>
                                                <div className="schema-zoom">
                                                    <button
                                                        onClick={() => {
                                                            wzoom.zoomDown();
                                                        }}
                                                    >
                                                        &#8722;
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            wzoom.zoomUp();
                                                        }}
                                                    >
                                                        &#43;
                                                    </button>
                                                </div>
                                                <div className="img-container">
                                                    {product?.schema && product?.fields?.schema && (
                                                        <img
                                                            alt=""
                                                            className="parts-img"
                                                            id="parts-img"
                                                            ref={imgRef}
                                                            src={
                                                                product?.fields?.schema?.[
                                                                    schemaIndex
                                                                ]?.childImageSharp?.gatsbyImageData
                                                                    ?.images?.fallback?.src
                                                            }
                                                            draggable={false}
                                                        />
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <img
                                        alt=""
                                        className="parts-img"
                                        id="parts-img"
                                        ref={imgRef}
                                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgMBAAZnY/0AAAAASUVORK5CYII="
                                        draggable={false}
                                    />
                                )}
                                {endsWithPdf && hasMultipleSchema && (
                                    <FontAwesomeIcon
                                        className="arrow right"
                                        icon={faChevronRight}
                                        onClick={() => updateSchemaIndex(1)}
                                    />
                                )}
                            </div>

                            <p>{product?.schema && t("reference-plan-message")}</p>
                            {isLoading ? (
                                <div className="part-loading-container">
                                    <LoadingFont />
                                </div>
                            ) : (
                                <table className="part-list">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>{t("part")}</th>
                                            <th></th>
                                            <th>&nbsp;</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {explodedParts
                                            .sort((a, b) =>
                                                comparePart(
                                                    a.part.attributes.schema_number?.values[0][
                                                        currentLocale
                                                    ],
                                                    b.part.attributes.schema_number?.values[0][
                                                        currentLocale
                                                    ]
                                                )
                                            )
                                            .map((part, index) => {
                                                return <Part key={index} part={part} />;
                                            })}
                                    </tbody>
                                </table>
                            )}
                            <br />
                            <p>{t("part-info1")}</p>
                            <p>{t("part-info2")}</p>
                            <p>{t("part-info3")}</p>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}
